import { useState } from "react";
import { toast } from "sonner";
import { usePanels } from "../../hooks/usePanels";
import { ITEMS, PANEL_ICON_MAP, PANEL_COLORS } from "../../config/panels";
import { RotateCcw, ArrowRight, LayoutGrid } from "lucide-react";
import { Button } from "../ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";

/**
 * UI to customise which items belong to which panel.
 * Storage is handled by PanelsProvider (localStorage).
 */
export const PanelsTab = () => {
  const { panels, movePanelItem, resetPanels } = usePanels();
  const [moving, setMoving] = useState(null);

  const handleMove = (itemId, targetPanelId) => {
    setMoving(itemId);
    movePanelItem(itemId, targetPanelId);
    const target = panels.find((p) => p.id === targetPanelId);
    toast.success(`Verschoben nach „${target?.label || targetPanelId}"`);
    setTimeout(() => setMoving(null), 250);
  };

  const handleReset = () => {
    if (!window.confirm("Panel-Struktur auf Standard zurücksetzen?")) return;
    resetPanels();
    toast.success("Panels zurückgesetzt");
  };

  return (
    <div className="space-y-6" data-testid="panels-tab">
      <div className="rounded-xl border border-slate-700/60 bg-slate-900/40 p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="p-2.5 rounded-lg bg-violet-500/15 text-violet-300 ring-1 ring-inset ring-violet-500/30">
              <LayoutGrid size={18} />
            </div>
            <div>
              <h3 className="text-base font-semibold text-white">Panel-Struktur</h3>
              <p className="text-sm text-slate-400 mt-1">
                Gruppiere Module in Panels. Die Sidebar zeigt nur die Inhalte des aktiven Panels —
                klickst du auf „Dashboard", siehst du die Panel-Übersicht.
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleReset}
            className="border-slate-600 text-slate-200 hover:bg-slate-800 shrink-0"
            data-testid="reset-panels-btn"
          >
            <RotateCcw size={14} className="mr-2" /> Zurücksetzen
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {panels.map((panel) => {
          const Icon = PANEL_ICON_MAP[panel.icon] || PANEL_ICON_MAP.LayoutDashboard;
          const colour = PANEL_COLORS[panel.color] || PANEL_COLORS.slate;

          return (
            <div
              key={panel.id}
              className={`rounded-xl border border-slate-700/60 bg-gradient-to-br ${colour.grad} bg-slate-900/40 p-5`}
              data-testid={`panel-config-${panel.id}`}
            >
              <div className="flex items-center gap-3 mb-4 pb-3 border-b border-slate-700/40">
                <div className={`p-2 rounded-lg bg-slate-800/60 ${colour.icon} ring-1 ring-inset ring-slate-700/50`}>
                  <Icon size={18} />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-semibold text-white">{panel.label}</h4>
                  <p className="text-xs text-slate-400 truncate">{panel.description}</p>
                </div>
                <span className="text-[10.5px] uppercase tracking-wider text-slate-400 px-2 py-0.5 rounded-full bg-slate-800/70">
                  {panel.items.length}
                </span>
              </div>

              {panel.items.length === 0 ? (
                <p className="text-xs text-slate-500 italic py-4 text-center">
                  Keine Module zugewiesen
                </p>
              ) : (
                <ul className="space-y-1.5">
                  {panel.items.map((itemId) => {
                    const item = ITEMS[itemId];
                    if (!item) return null;
                    const ItemIcon = item.icon;

                    return (
                      <li
                        key={itemId}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-800/40 border border-slate-700/40 transition-opacity ${
                          moving === itemId ? "opacity-40" : ""
                        }`}
                        data-testid={`panel-item-${itemId}`}
                      >
                        <ItemIcon size={14} className="text-slate-400 shrink-0" />
                        <span className="text-sm text-slate-200 flex-1 truncate">{item.label}</span>

                        <Select
                          value={panel.id}
                          onValueChange={(value) => {
                            if (value !== panel.id) handleMove(itemId, value);
                          }}
                        >
                          <SelectTrigger
                            className="h-7 w-auto min-w-[110px] text-[11px] border-slate-600/60 bg-slate-900/60 text-slate-300"
                            data-testid={`move-${itemId}`}
                          >
                            <ArrowRight size={11} className="mr-1 opacity-60" />
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {panels.map((p) => (
                              <SelectItem key={p.id} value={p.id} className="text-xs">
                                {p.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PanelsTab;
